/**
 * notices — facilitator notifications (#14–21), on Scoring's contract v1.1
 * (scoring repo docs/NOTIFICATIONS_CONTRACT.md, 11 Oct; v1.2 renamed `urgent` to
 * `is_urgent` and `action` to `button` before launch).
 *
 * Only the logic lives here: which Studio page a notice opens, how notices
 * fold into cards, which pop-up comes next and when to ask the server.
 * The bell, list and pop-up are drawn after Nino approves Design's sketch.
 */
import type { FacilitatorPage } from '@/types/facilitator-portal';

export type NoticeKind = 'celebrate' | 'get_ready' | 'heads_up' | 'invite';

export interface NoticeButton {
  label: string;
  screen: string;
  params: Record<string, string>;
}

export interface Notice {
  id: string;
  type: string;
  kind: NoticeKind;
  is_urgent: boolean;
  popup: boolean;
  title: string;
  message: string;
  button: NoticeButton | null;
  group_key: string | null;
  organisation: { id: string; name: string } | null;
  data: Record<string, unknown>;
  createdAt: string;
  readAt: string | null;
  shownAt: string | null;
}

export interface NoticeList {
  items: Notice[];
  unread_count: number;
}

/** Ask the server every 30 s while Studio is open and in front (Scoring: fine
 *  for the server; a notice is in the list within 5 s of its event). */
export const NOTICE_POLL_MS = 30_000;

/** No pop-ups in the drill console (Design's rule; "learner waiting" already
 *  shows in the console's lobby), nor while the tab is hidden. */
export function shouldAskForPopups(o: { visible: boolean; inConsole: boolean }): boolean {
  return o.visible && !o.inConsole;
}

/** Where a notice takes the facilitator. `null` when the screen is one Studio
 *  doesn't know (a newer server): the notice shows, with no button, rather
 *  than a button that goes nowhere. */
export type NoticeTarget = { page: FacilitatorPage; sessionId?: string; drillId?: string; openConsole?: boolean };

export function noticeTarget(button: NoticeButton | null): NoticeTarget | null {
  if (!button) return null;
  const sessionId = button.params?.session_id || undefined;
  switch (button.screen) {
    case 'session': return sessionId ? { page: 'sessions', sessionId } : null;
    case 'console': return sessionId ? { page: 'sessions', sessionId, openConsole: true } : null;
    // #14 (12 Oct): a learner asked for a time on a drill this facilitator
    // runs; the button names the drill, so it opens Drills at that drill's
    // Claim a time. A claim naming a session is a proposal offered to them.
    case 'claim': {
      const drillId = button.params?.drill_id || undefined;
      if (drillId) return { page: 'active-drills', drillId };
      return sessionId ? { page: 'proposals', sessionId } : null;
    }
    case 'review': return sessionId ? { page: 'wrapup', sessionId } : null;
    case 'schedule': return { page: 'schedule' };
    default: return null;
  }
}

/** One card in the list: a single notice, or several sharing a group_key
 *  ("3 changes to Thursday's drill"), newest first. */
export interface NoticeCard {
  key: string;
  notices: Notice[];
  unread: boolean;
}

const time = (n: Notice) => Date.parse(n.createdAt) || 0;

/** Seat changes (#20) fold per session. The server sends them as seat_booked /
 *  seat_left without a group_key yet (asked, 12 Oct), so Studio folds them by
 *  the session their button opens. */
const SEAT_TYPES = new Set(['seat_booked', 'seat_left', 'seats_changed']);

export function groupKeyOf(n: Notice): string {
  if (n.group_key) return `g:${n.group_key}`;
  const sid = n.button?.params?.session_id;
  if (SEAT_TYPES.has(n.type) && sid) return `seat:${sid}`;
  return `n:${n.id}`;
}

export function cardsFor(items: Notice[]): NoticeCard[] {
  const sorted = [...items].sort((a, b) => time(b) - time(a));
  const byKey = new Map<string, NoticeCard>();
  const cards: NoticeCard[] = [];
  for (const n of sorted) {
    const key = groupKeyOf(n);
    const card = byKey.get(key);
    if (card) {
      card.notices.push(n);
      card.unread ||= !n.readAt;
    } else {
      const fresh = { key, notices: [n], unread: !n.readAt };
      byKey.set(key, fresh);
      cards.push(fresh);
    }
  }
  return cards;
}

/** What the pop-up shows next (Design, 11 Oct): one at a time, urgent first;
 *  3 or more arriving within 10 s fold into one "N new notifications" card. */
export type PopupStep =
  | { kind: 'one'; notice: Notice }
  | { kind: 'many'; count: number; ids: string[] };

const FOLD_WINDOW_MS = 10_000;

export function nextPopup(pending: Notice[]): PopupStep | null {
  const waiting = pending.filter((n) => n.popup && !n.shownAt);
  if (waiting.length === 0) return null;
  const urgent = waiting.filter((n) => n.is_urgent).sort((a, b) => time(a) - time(b));
  if (urgent.length > 0) return { kind: 'one', notice: urgent[0] };
  const calm = waiting.sort((a, b) => time(a) - time(b));
  const newest = time(calm[calm.length - 1]);
  const burst = calm.filter((n) => newest - time(n) <= FOLD_WINDOW_MS);
  if (burst.length >= 3) return { kind: 'many', count: burst.length, ids: burst.map((n) => n.id) };
  return { kind: 'one', notice: calm[0] };
}

/** The list as Design drew it (12 Oct): an unread "learner waiting" pinned on
 *  top while it is still true (the server marks it read when the room starts),
 *  then Today, Earlier this week and Earlier, newest first. */
export interface NoticeSections {
  pinned: NoticeCard[];
  today: NoticeCard[];
  week: NoticeCard[];
  earlier: NoticeCard[];
}

const DAY_MS = 86_400_000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function sectionsFor(items: Notice[], now: Date): NoticeSections {
  const out: NoticeSections = { pinned: [], today: [], week: [], earlier: [] };
  const today = startOfDay(now);
  for (const card of cardsFor(items)) {
    const first = card.notices[0];
    if (card.notices.length === 1 && first.is_urgent && !first.readAt) { out.pinned.push(card); continue; }
    const at = time(first);
    if (at >= today) out.today.push(card);
    else if (at >= today - 6 * DAY_MS) out.week.push(card);
    else out.earlier.push(card);
  }
  return out;
}

/** How long ago, in the row's corner: now, 12 min, 10:12 today, a weekday this
 *  week, else a date. Returns the parts; the caller words them. */
export type Ago = { kind: 'now' } | { kind: 'min'; n: number } | { kind: 'time' | 'weekday' | 'date'; at: Date };

export function agoOf(createdAt: string, now: Date): Ago {
  const at = new Date(createdAt);
  const diff = now.getTime() - at.getTime();
  if (!(diff >= 0) || diff < 60_000) return { kind: 'now' };
  if (diff < 60 * 60_000) return { kind: 'min', n: Math.floor(diff / 60_000) };
  const today = startOfDay(now);
  if (at.getTime() >= today) return { kind: 'time', at };
  if (at.getTime() >= today - 6 * DAY_MS) return { kind: 'weekday', at };
  return { kind: 'date', at };
}

/** The bell's count: "9+" above nine, nothing at zero. */
export function bellCount(unread: number): string | null {
  if (!(unread > 0)) return null;
  return unread > 9 ? '9+' : String(unread);
}
